// Sales pipeline board (2026-06-12). The rep's daily deal cockpit: every
// open deal as a card, columns by CRM stage, drag a card to advance it,
// quick-advance / mark-lost actions, and a rust flag on anything stalling
// past the follow-up window.
//
// Reads GET /sales/pipeline?scope=. Stage moves PATCH /projects/:id
// { status_stage } (the existing route) and refetch. Deal value is the
// project budget for now (wiring it to the live estimate total is a
// tracked fast-follow). Cards open the project in the SAME tab (house UX
// standard, 2026-07-24). Because a same-tab open remounts the board on
// Back, the scope filter lives in the URL (?scope=all) so Back restores
// the board exactly as the rep left it — same useSearchParams pattern as
// BillingPage / InvoicingPage.
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  Clock,
  Flag,
  GripVertical,
  PauseCircle,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { OpportunityBoardView, type OpportunityBoardResponse } from "@/components/opportunity/OpportunityBoard";

type Stage = "opportunity" | "presentation" | "estimate" | "sales_order";

interface Deal {
  id: string;
  projectNumber: number;
  name: string;
  clientId: string;
  clientName: string;
  stage: Stage;
  state: "open" | "on_hold" | "closed";
  value: number;
  currency: string;
  clientRepId: string;
  clientRepName: string | null;
  orderRepId: string | null;
  inHandsDate: string;
  lastActivityAt: string | null;
  ageDays: number;
  stalling: boolean;
}

interface Column {
  stage: Stage;
  deals: Deal[];
  count: number;
  totalValue: number;
}

interface PipelineResponse {
  columns: Column[];
  metrics: { openValue: number; activeDeals: number; avgAgeDays: number };
}

const STAGE_ORDER: Stage[] = [
  "opportunity",
  "presentation",
  "estimate",
  "sales_order",
];

const STAGE_LABEL: Record<Stage, string> = {
  opportunity: "Opportunity",
  presentation: "Presentation",
  estimate: "Estimate",
  sales_order: "Quote",
};

// Advancing past sales_order hands the deal to production (off the board).
const NEXT_STAGE: Record<Stage, string> = {
  opportunity: "presentation",
  presentation: "estimate",
  estimate: "sales_order",
  sales_order: "in_production",
};

function money(n: number): string {
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function ageLabel(d: Deal): string {
  if (d.ageDays <= 0) return "today";
  return `${d.ageDays}d`;
}

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: string } | undefined;
    return body?.error ?? `Request failed (${e.status})`;
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

export function SalesPipelinePage() {
  const queryClient = useQueryClient();
  // Scope persists to the URL so a same-tab card open + browser Back
  // restores the board's filter (replace, not push — every toggle
  // updates the board's own history entry rather than stacking).
  const [searchParams, setSearchParams] = useSearchParams();
  // Everyone starts on their own deals (Stephanie 2026-10-16); All deals is
  // ?scope=all.
  const scope: "all" | "mine" =
    searchParams.get("scope") === "all" ? "all" : "mine";
  const setScope = (s: "all" | "mine"): void => {
    const next = new URLSearchParams(searchParams);
    if (s === "mine") next.delete("scope");
    else next.set("scope", s);
    setSearchParams(next, { replace: true });
  };
  const [banner, setBanner] = useState<{ kind: "ok" | "err"; msg: string } | null>(
    null,
  );
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<Stage | "lost" | null>(null);

  const queryKey = ["sales", "pipeline", scope];
  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => api<PipelineResponse>(`/sales/pipeline?scope=${scope}`),
  });

  // Opportunity stages (CEO 2026-10-15): once switched on, the board shows
  // Open / Awaiting Approval / Won / Lost instead of the old document steps.
  const board = useQuery({
    queryKey: ["sales", "opportunity-board", scope],
    queryFn: () => api<OpportunityBoardResponse>(`/sales/opportunity-board?scope=${scope}`),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["sales", "pipeline"] });

  const move = useMutation({
    mutationFn: (vars: { id: string; stage: string }) =>
      api(`/projects/${vars.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status_stage: vars.stage }),
      }),
    onSuccess: (_res, vars) => {
      if (vars.stage === "lost") setBanner({ kind: "ok", msg: "Deal marked lost." });
      else if (vars.stage === "in_production")
        setBanner({ kind: "ok", msg: "Deal won — handed to production." });
      else setBanner({ kind: "ok", msg: "Deal moved." });
      invalidate();
    },
    onError: (e) => setBanner({ kind: "err", msg: errText(e) }),
  });

  const columns = data?.columns ?? [];
  const metrics = data?.metrics;

  const byStage = useMemo(() => {
    const m = new Map<Stage, Column>();
    for (const c of columns) m.set(c.stage, c);
    return m;
  }, [columns]);

  function moveTo(deal: Deal, stage: string) {
    if (stage === deal.stage) return;
    setBanner(null);
    move.mutate({ id: deal.id, stage });
  }

  function onDrop(stage: Stage) {
    const id = draggingId;
    setDraggingId(null);
    setDragOver(null);
    if (!id) return;
    const deal = columns.flatMap((c) => c.deals).find((d) => d.id === id);
    if (deal) moveTo(deal, stage);
  }

  if (board.data?.available) {
    return <OpportunityBoardView board={board.data} scope={scope} setScope={setScope} />;
  }

  return (
    <div className="mx-auto w-full">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
            Sales · Pipeline
          </p>
          <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
            Deal pipeline
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-brown-400">
            Every open deal by stage. Drag a card to move it forward, or use
            the actions on each card.
          </p>
        </div>
        <div className="inline-flex overflow-hidden rounded-lg border border-brown-200">
          {(["mine", "all"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setScope(s)}
              className={cn(
                "px-3.5 py-1.5 text-sm font-semibold transition-colors",
                scope === s
                  ? "bg-brown-500 text-cream-50"
                  : "bg-white text-brown-400 hover:bg-cream-50",
              )}
            >
              {s === "all" ? "All deals" : "My deals"}
            </button>
          ))}
        </div>
      </div>

      {/* Metrics */}
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Metric label="Open pipeline" value={metrics ? money(metrics.openValue) : "—"} />
        <Metric label="Active deals" value={metrics ? String(metrics.activeDeals) : "—"} />
        <Metric
          label="Avg. age"
          value={metrics ? `${metrics.avgAgeDays} days` : "—"}
        />
      </div>

      {/* Banner */}
      {banner && (
        <div
          className={cn(
            "mb-6 rounded-lg border px-4 py-3 text-sm",
            banner.kind === "ok"
              ? "border-brown-100 bg-cream-50 text-brown-500"
              : "border-rust-200 bg-rust-50 text-rust-600",
          )}
        >
          {banner.msg}
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-brown-300">Loading pipeline…</p>
      ) : isError ? (
        <p className="text-sm text-rust-600">Couldn't load the pipeline.</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          {STAGE_ORDER.map((stage) => {
            const col = byStage.get(stage);
            const deals = col?.deals ?? [];
            return (
              <div
                key={stage}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(stage);
                }}
                onDragLeave={() => setDragOver((s) => (s === stage ? null : s))}
                onDrop={() => onDrop(stage)}
                className={cn(
                  "flex flex-col rounded-xl border bg-cream-50/60 p-2.5 transition-colors",
                  dragOver === stage
                    ? "border-rust-300 bg-rust-50/50"
                    : "border-brown-100",
                )}
              >
                <div className="mb-2.5 flex items-baseline justify-between border-b border-brown-100 px-1 pb-2">
                  <span className="text-sm font-bold text-brown-500">
                    {STAGE_LABEL[stage]}
                  </span>
                  <span className="text-xs text-brown-300">
                    {col?.count ?? 0} · {money(col?.totalValue ?? 0)}
                  </span>
                </div>

                <div className="flex min-h-[120px] flex-col gap-2">
                  {deals.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-brown-100 p-6 text-center text-xs text-brown-300">
                      No deals here.
                    </div>
                  ) : (
                    deals.map((deal) => (
                      <DealCard
                        key={deal.id}
                        deal={deal}
                        dragging={draggingId === deal.id}
                        busy={move.isPending}
                        onDragStart={() => setDraggingId(deal.id)}
                        onDragEnd={() => {
                          setDraggingId(null);
                          setDragOver(null);
                        }}
                        onAdvance={() => moveTo(deal, NEXT_STAGE[deal.stage])}
                        onLost={() => moveTo(deal, "lost")}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-brown-100 bg-white px-4 py-3">
      <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
        {label}
      </div>
      <div className="mt-1 font-display text-2xl font-semibold text-brown-500">
        {value}
      </div>
    </div>
  );
}

function DealCard({
  deal,
  dragging,
  busy,
  onDragStart,
  onDragEnd,
  onAdvance,
  onLost,
}: {
  deal: Deal;
  dragging: boolean;
  busy: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onAdvance: () => void;
  onLost: () => void;
}) {
  const isWon = deal.stage === "sales_order";
  const navigate = useNavigate();
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", deal.id);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      className={cn(
        "group rounded-lg border bg-white p-3 transition-shadow",
        dragging ? "opacity-50" : "hover:shadow-sm",
        deal.stalling ? "border-rust-300" : "border-brown-100",
      )}
    >
      <div className="flex items-start gap-2">
        <GripVertical className="mt-0.5 h-4 w-4 shrink-0 cursor-grab text-muted" />
        <button
          type="button"
          onClick={() => navigate(`/projects/${deal.id}`)}
          className="min-w-0 flex-1 text-left"
          title="Open project"
        >
          <div className="truncate text-sm font-semibold text-brown-500">
            {deal.clientName}
          </div>
          <div className="truncate text-xs text-brown-400">{deal.name}</div>
        </button>
      </div>

      <div className="mt-2 flex items-center justify-between">
        <span className="text-sm font-bold text-brown-500 tabular-nums">
          {money(deal.value)}
        </span>
        <span
          className={cn(
            "inline-flex items-center gap-1 text-xs",
            deal.stalling ? "font-semibold text-rust-500" : "text-brown-300",
          )}
        >
          {deal.stalling ? (
            <AlertTriangle className="h-3.5 w-3.5" />
          ) : (
            <Clock className="h-3.5 w-3.5" />
          )}
          {ageLabel(deal)}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {deal.state === "on_hold" && (
          <span className="inline-flex items-center gap-1 rounded-full bg-cream-100 px-2 py-0.5 text-[11px] font-semibold text-brown-400">
            <PauseCircle className="h-3 w-3" /> On hold
          </span>
        )}
        {deal.clientRepName && (
          <span className="inline-flex items-center gap-1 text-[11px] text-brown-300">
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-peach-400 text-[9px] font-bold text-white">
              {deal.clientRepName.charAt(0).toUpperCase()}
            </span>
            {deal.clientRepName.split(" ")[0]}
          </span>
        )}
      </div>

      <div className="mt-2.5 flex items-center gap-2 border-t border-brown-50 pt-2">
        <button
          type="button"
          disabled={busy}
          onClick={onAdvance}
          className="inline-flex items-center gap-1 text-xs font-semibold text-ink hover:text-muted disabled:opacity-50"
          title={isWon ? "Hand off to production" : "Advance to the next stage"}
        >
          {isWon ? "Won → Production" : "Advance"}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
        {!isWon && (
          <button
            type="button"
            disabled={busy}
            onClick={onLost}
            className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-brown-300 hover:text-rust-500 disabled:opacity-50"
            title="Mark this deal lost"
          >
            <Flag className="h-3.5 w-3.5" /> Lost
          </button>
        )}
      </div>
    </div>
  );
}
